/* ---------------------------------------------------------------------------
   TRACK YOUR ORDER

   The guest lookup. Posts the order number and email to the app proxy, which
   only answers when both match the same order, and renders the thin reply it
   gets back.

   Deliberately says the same thing for "no such order" and "wrong email" --
   the worker does not distinguish between them either, so that this form
   cannot be used to find out which order numbers exist.
   --------------------------------------------------------------------------- */
(function () {
  class SynergyTrackOrder extends HTMLElement {
    connectedCallback() {
      this.path = this.getAttribute('data-track-path') || '/apps/wishlist/track';
      this.form = this.querySelector('[data-track-form]');
      this.message = this.querySelector('[data-track-message]');
      this.result = this.querySelector('[data-track-result]');
      this.button = this.querySelector('[data-track-submit]');
      if (!this.form) return;

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.lookup();
      });
    }

    say(text, isError) {
      this.message.textContent = text;
      this.message.hidden = !text;
      this.message.classList.toggle('syn-track__message--error', !!isError);
    }

    async lookup() {
      const email = this.form.elements.email.value.trim();
      const orderNumber = this.form.elements.orderNumber.value.trim();

      this.form.elements.email.setAttribute('aria-invalid', email.indexOf('@') < 1 ? 'true' : 'false');
      this.form.elements.orderNumber.setAttribute('aria-invalid', orderNumber ? 'false' : 'true');

      if (email.indexOf('@') < 1 || !orderNumber) {
        this.result.hidden = true;
        this.say('Please enter both the email address on the order and the order number.', true);
        return;
      }

      this.result.hidden = true;
      this.say('Looking up your order…');
      this.button.disabled = true;

      let data;
      try {
        const res = await fetch(this.path, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email, orderNumber: orderNumber }),
        });
        data = await res.json();
      } catch (e) {
        data = { error: 'unavailable' };
      }
      this.button.disabled = false;

      if (data && data.error) {
        this.say(
          data.error === 'unavailable'
            // Plain wording, not a link: say() writes textContent, so markup
            // would be shown literally.
            ? 'We could not reach our order system just now. Please try again in a moment, or send us a message from our Contact Us page.'
            : data.error,
          true
        );
        return;
      }

      if (!data || !data.found) {
        this.say(
          'We could not find an order with that number and email address. Please check both against your confirmation email, or send us a message from our Contact Us page and we will look it up for you.',
          true
        );
        return;
      }

      this.say('');
      this.render(data);
    }

    render(order) {
      const rows = [];
      const row = (label, value) => {
        if (!value) return;
        rows.push('<div class="syn-track__row"><dt>' + label + '</dt><dd>' + value + '</dd></div>');
      };

      const date = order.placedAt
        ? new Date(order.placedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

      /* Shopify's enum values are SCREAMING_SNAKE; make them read like English. */
      const pretty = (s) =>
        String(s || '')
          .toLowerCase()
          .split('_')
          .join(' ')
          .replace(/^./, (c) => c.toUpperCase());

      row('Placed', date);
      row('Payment', pretty(order.payment));
      row('Delivery', order.cancelled ? 'Cancelled' : pretty(order.fulfillment));
      if (order.total) {
        row('Total', (order.currency === 'USD' ? '$' : '') + Number(order.total).toFixed(2));
      }

      let tracking = '';
      if (order.tracking && order.tracking.length) {
        const items = order.tracking.map((t) => {
          const label = [t.company, t.number].filter(Boolean).join(' ') || 'Track this shipment';
          return t.url
            ? '<p><a href="' + t.url + '" target="_blank" rel="noopener">' + label + '</a></p>'
            : '<p>' + label + '</p>';
        });
        tracking = '<div class="syn-track__tracking">' + items.join('') + '</div>';
      } else {
        tracking =
          '<div class="syn-track__tracking"><p>No tracking yet. It appears here, and in your shipping confirmation email, once the parcel is scanned by the carrier.</p></div>';
      }

      this.result.innerHTML =
        '<p class="syn-track__result-name">Order ' + order.name + '</p><dl>' + rows.join('') + '</dl>' + tracking;
      this.result.hidden = false;
    }
  }

  if (!customElements.get('synergy-track-order')) {
    customElements.define('synergy-track-order', SynergyTrackOrder);
  }
})();
